// --- React & Redux
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
// --- Actions & Hooks
import { fetchUserPermissions, logout } from "../../tap/store/auth";
// --- External UI Libraries
import LottiePlayer from "@dtci/spellbook/LottiePlayer";
// --- Utilities
import { getAuthToken } from "tap/utils/localStorageService.js";
// --- Config
import { API_BASE } from "tap/config/constants.js";

/**
 * SecuredApp - Authentication wrapper component
 *
 * Verifies the user's token with the backend before granting access to the app.
 * Redirects to login if token is invalid or missing.
 *
 * @param {Object} props
 * @param {React.ComponentType} props.App - The main application component to render when authenticated
 */
export function SecuredApp({ App }) {
  const [isAuth, setIsAuth] = useState(false); // SECURITY: Default to false, verify first
  const [isVerifying, setIsVerifying] = useState(true);
  const [sessionInvalidated, setSessionInvalidated] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    verifyTokenWithBackend();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isVerifying) {
      giveAccess();
    }
    // eslint-disable-next-line
  }, [isVerifying]);

  async function verifyTokenWithBackend() {
    const token = getAuthToken();
    if (!token?.length) {
      setIsVerifying(false);
      setIsAuth(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/users/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.authenticated) {
          dispatch(fetchUserPermissions(token));
          setIsAuth(true);
        } else {
          console.warn("Token not valid - clearing");
          clearInvalidToken();
        }
      } else if (response.status === 401 || response.status === 403) {
        console.warn(
          "Token validation failed (401/403) - clearing invalid token",
        );
        clearInvalidToken();
      } else {
        console.warn(
          "Token verification returned unexpected status:",
          response.status,
        );
        clearInvalidToken();
      }
    } catch (error) {
      console.error("Token verification failed:", error);
      clearInvalidToken();
    } finally {
      setIsVerifying(false);
    }
  }

  function clearInvalidToken() {
    setSessionInvalidated(true);
    dispatch(logout());
    setIsAuth(false);
  }

  function giveAccess() {
    const token = getAuthToken();
    if (isAuth && token) {
      return;
    } else if (!token || !isAuth) {
      let loginPath = "/login";
      if (location.pathname !== "/login") {
        const target = location.pathname + location.search;
        loginPath = `/login?postLoginRedirect=${encodeURIComponent(target)}`;
      }
      if (!sessionInvalidated) {
        dispatch(logout());
      }
      setIsAuth(false);
      navigate(loginPath);
    }
  }

  // Minimal loading spinner during auth verification - avoids flashing the login page
  if (isVerifying) {
    return (
      <div
        style={{
          display: "grid",
          placeItems: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <LottiePlayer style={{ width: "120px", height: "120px" }} />
      </div>
    );
  }

  if (isAuth) {
    return <App />;
  }

  // Fallback while redirecting to login
  return null;
}
